import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { MainContext } from "../../../main/context/MainContextProvider";
import {
  cardInfoIdentifiersToCardInfo,
  cardInfoToCardInfoIdentifiers,
  getCardsInfoWithUpdatedProps,
  getDefaultCardsConfig,
  getDefaultPropsByCardType,
} from "../../../main/themes/defaultLayout";
import {
  defaultCardsDisplayableColorProps,
  defaultCardsDisplayableProps,
  defaultLayouts,
  defaultPortfolioData,
} from "../../../main/themes/defaultLayoutData";
import { DadosIndicadoresEventManager } from "../helpers/DadosIndicadoresEventManager";
import { DadosPortfolioEventManager } from "../helpers/DadosPortfolioEventManager";
import {
  ropCardEventToCard,
  type CardTypeAndData,
  getRopCardName,
} from "../helpers/dados-indicadores-converter";
import {
  CardTypesEnum,
  type CardInfo,
  type CardsDisplayableProps,
  type ItemLayoutsConfig,
  CardChildrenProps,
  CardInfoIdentifiers,
  CardsDisplayableColorProps,
  GetRopCardParams,
  EquipmentFilter,
} from "../types";
import { ropSSEEventToPortfolioData } from "../helpers/portfolio-converter";
import { PortfolioData } from "../../portfolio/types";
import { ropSSEAlarmToAlarmNotificationPropsArray } from "../helpers/alarm-converter";
import { AlarmNotificationProps } from "../../../main/components/AlarmNotification";
import { usersApi, usinasApi } from "../../../services/api";
import { RopCard } from "../models/dados-indicadores.model";
import { useClientClusterUsinaFilterContext } from "../../../main/context/ClientClusterUsinaFilterContextProvider";
import { useLocation } from "react-router-dom";

function mergeStoredDisplayableProps(
  defaults: CardsDisplayableProps,
  stored: Partial<CardsDisplayableProps>,
): CardsDisplayableProps {
  const out: Record<string, unknown> = { ...defaults };
  (Object.keys(defaults) as (keyof CardsDisplayableProps)[]).forEach((key) => {
    const storedVal = stored[key];
    if (storedVal === undefined) return;
    const defaultVal = defaults[key];
    if (
      key !== "id" &&
      defaultVal !== null &&
      typeof defaultVal === "object" &&
      !Array.isArray(defaultVal) &&
      typeof storedVal === "object" &&
      storedVal !== null &&
      !Array.isArray(storedVal)
    ) {
      out[key as string] = {
        ...(defaultVal as Record<string, unknown>),
        ...(storedVal as Record<string, unknown>),
      };
    } else {
      out[key as string] = storedVal;
    }
  });
  return out as CardsDisplayableProps;
}

type DashboardContextType = {
  layoutsConfig: ItemLayoutsConfig;
  setLayoutsConfig: (layouts: ItemLayoutsConfig) => void;
  defaultLayouts: ItemLayoutsConfig;
  cardsConfig: CardInfo[];
  setCardsConfig: (cardConfigs: CardInfo[]) => void;
  cardsDisplayableProps: CardsDisplayableProps;
  setCardsDisplayableProps: (props: CardsDisplayableProps) => void;
  cardsDisplayableColorProps: CardsDisplayableColorProps;
  setCardsDisplayableColorProps: (props: CardsDisplayableColorProps) => void;
  canEditLayout: boolean;
  setCanEditLayout: (canEdit: boolean) => void;
  getChildrenPropsByCardType(
    cardType: CardTypesEnum
  ): CardChildrenProps | undefined;
  portfolioData: PortfolioData;
  showPotenciaAtivaInCharts: boolean;
  setShowPotenciaAtivaInCharts: Dispatch<SetStateAction<boolean>>;
  activeAlarms: AlarmNotificationProps[];
  requestParamCards: string[];
  setRequestParamCards: (requestParamCards: string[]) => void;
  dashboardHistoryCards: CardTypeAndData[];
  equipmentFilter: EquipmentFilter;
  setEquipmentFilter: (equipmentFilter: EquipmentFilter) => void;
  cardIsLoading: boolean;
};

const defaultValue: DashboardContextType = {
  layoutsConfig: {
    id: "",
    layouts: {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    },
  },
  setLayoutsConfig: () => { },
  defaultLayouts: {
    id: "",
    layouts: {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    },
  },
  cardsConfig: [],
  setCardsConfig: () => { },
  cardsDisplayableProps: defaultCardsDisplayableProps,
  setCardsDisplayableProps: () => { },
  cardsDisplayableColorProps: defaultCardsDisplayableColorProps,
  setCardsDisplayableColorProps: () => { },
  canEditLayout: false,
  setCanEditLayout: () => { },
  getChildrenPropsByCardType: () => undefined,
  portfolioData: {
    energiaGeradaTotal: "",
    performanceRatio: "",
    disponibilidade: "",
    receitaEstimada: "",
    irradianciaMedia: "",
    temperaturaMedia: "",
    inversoresOffline: "",
    inversoresBaixaEficiencia: "",
    usinas: [],
    potenciaAtiva: "",
    potenciaEsperada: "",
    energiaIdeal: "",
    energiaPrevista: "",
    prRealizado: "",
    prPrevisto: "",
    energiaGeradaAtDayByHour: "",
    irradianciaMediaByHour: "",
    irradianciaMediaAtMinutes: "",
    potenciaAtivaAtMinutes: "",
  },
  showPotenciaAtivaInCharts: false,
  setShowPotenciaAtivaInCharts: () => { },
  activeAlarms: [],
  requestParamCards: [],
  setRequestParamCards: () => { },
  dashboardHistoryCards: [],
  equipmentFilter: {},
  setEquipmentFilter: () => { },
  cardIsLoading: false,
};

export const DashboardContext = React.createContext(defaultValue);

export default function DashboardContextProvider(props: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const shouldKeepSSEConnectionOpen =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/portfolio");

  const {
    user,
    selectedDashboardUsina,
    selectedDashboardClient,
    toastNotifications,
    setToastNotifications,
  } = useContext(MainContext);
  const {
    selectedClientFilter,
    selectedCluster,
  } = useClientClusterUsinaFilterContext();

  function getStoredLayouts() {
    try {
      const savedLayouts = localStorage.getItem("layoutsConfig");
      if (savedLayouts) {
        return JSON.parse(savedLayouts);
      }
    } catch {
      localStorage.clear();
    }
    return defaultLayouts;
  }
  function getStoredDisplayableProps(): CardsDisplayableProps {
    try {
      const savedDisplayableProps = localStorage.getItem(
        "cardsDisplayableProps"
      );
      if (savedDisplayableProps) {
        const parsed = JSON.parse(savedDisplayableProps) as Partial<CardsDisplayableProps>;
        return mergeStoredDisplayableProps(defaultCardsDisplayableProps, parsed);
      }
    } catch {
      localStorage.clear();
    }
    return defaultCardsDisplayableProps;
  }
  function getStoredDisplayableColorProps() {
    const savedDisplayableColorProps = localStorage.getItem(
      "cardsDisplayableColorProps"
    );
    if (savedDisplayableColorProps) {
      return JSON.parse(savedDisplayableColorProps);
    }
    return defaultCardsDisplayableColorProps;
  }
  const [cardsConfig, setCardsConfig] = React.useState<CardInfo[]>(
    getStoredCardsConfig()
  );
  function getStoredCardsConfig(): CardInfo[] {
    try {
      const savedCardsConfigIdentifiers = localStorage.getItem("cardsConfig");
      if (savedCardsConfigIdentifiers) {
        const cardsConfigIdentifiers: CardInfoIdentifiers[] = JSON.parse(
          savedCardsConfigIdentifiers
        );
        const savedCardsConfig = cardsConfigIdentifiers.map(
          (cardInfoIdentifiers) => {
            const props = getDefaultPropsByCardType(cardInfoIdentifiers.type);
            return cardInfoIdentifiersToCardInfo(cardInfoIdentifiers, props);
          }
        );
        return savedCardsConfig;
      }
    } catch (error) {
      console.error(error);
      localStorage.clear();
    }
    return getDefaultCardsConfig();
  }
  const [layoutsConfig, setLayoutsConfig] = React.useState<ItemLayoutsConfig>(
    getStoredLayouts()
  );
  const [cardsDisplayableProps, setCardsDisplayableProps] =
    React.useState<CardsDisplayableProps>(getStoredDisplayableProps());
  const [cardsDisplayableColorProps, setCardsDisplayableColorProps] =
    React.useState<CardsDisplayableColorProps>(
      getStoredDisplayableColorProps()
    );
  const [canEditLayout, setCanEditLayout] = React.useState(false);

  const [portfolioData, setPortfolioData] =
    React.useState<PortfolioData>(defaultPortfolioData);

  const [showPotenciaAtivaInCharts, setShowPotenciaAtivaInCharts] =
    React.useState(false);
  const [requestParamCards, setRequestParamCards] = useState<string[]>([]);

  const [dashboardHistoryCards, setDashboardHistoryCards] = useState<
    CardTypeAndData[]
  >([]);

  const [cardIsLoading, setCardIsLoading] = useState(false);

  const [equipmentFilter, setEquipmentFilter] = useState<EquipmentFilter>({});

  useEffect(() => {
    onMount();
  }, []);
  const [activeAlarms, setActiveAlarms] = React.useState<
    AlarmNotificationProps[]
  >([]);

  const convertStringsToCards = (cards: string[]) => {
    const ropCards: CardTypeAndData[] = [];
    cards.forEach((card) => {
      let ropCardUpdate: RopCard;
      let updatedCardConfig: CardTypeAndData;
      try {
        const parsedEvtData = JSON.parse(card);
        ropCardUpdate = parsedEvtData;
      } catch (error) {
        console.error(error, "Erro ao fazer parse do evento RopCardUpdate");
        return;
      }
      try {
        updatedCardConfig = ropCardEventToCard(ropCardUpdate);
      } catch (error) {
        console.error(error, "Erro ao converter evento de card:");
        return;
      }
      console.debug("Card atualizado:", updatedCardConfig);
      ropCards.push(updatedCardConfig);
    });
    return ropCards;
  };

  const getRequestCards = async () => {
    if (requestParamCards.length === 0) {
      setDashboardHistoryCards([]);
      return;
    }
    if (!selectedDashboardUsina) {
      //TODO: Mostrar erro
      return;
    }
    setCardIsLoading(true);
    const response = await usinasApi.getDashboardHistory(
      selectedDashboardUsina.usinaIds.ropInputId,
      requestParamCards
    );
    setCardIsLoading(false);
    const cards = convertStringsToCards(response.data);
    const parsedRequestParamsCards: GetRopCardParams[] = requestParamCards.map(
      (param) => JSON.parse(param)
    );
    const notReceivedCards = parsedRequestParamsCards.filter(
      (card) => !cards.find((c) => c.ropCard.type === card.ropCardType)
    );
    if (notReceivedCards.length > 0) {
      const newNotifications = {
        isAdding: true,
        notifications: [
          ...toastNotifications.notifications,
          {
            severity: "warning" as const,
            text: `Não foi possível obter os dados de ${notReceivedCards
              .map((card) => getRopCardName(card.ropCardType))
              .join(", ")}.`,
          },
        ],
      };
      setToastNotifications(newNotifications);

      const newRequestParamCards = parsedRequestParamsCards
        .filter(
          (card) =>
            !notReceivedCards.find((c) => c.ropCardType === card.ropCardType)
        )
        .map((card) => JSON.stringify(card));

      setRequestParamCards(newRequestParamCards);
    }
    setDashboardHistoryCards(cards);
  };

  useEffect(() => {
    if (requestParamCards.length > 0 || dashboardHistoryCards.length > 0) {
      getRequestCards();
    }
  }, [requestParamCards]);

  const updateCards = (updatedCardConfig: CardTypeAndData) => {
    setCardsConfig((cardsConfig) => {
      const newCardsConfig = cardsConfig.map((cardConfig) => {
        if (
          dashboardHistoryCards.find(
            (card) => card.card.type == updatedCardConfig.card.type
          )
        ) {
          dashboardHistoryCards.map((card) => {
            return {
              ...cardConfig,
              childrenProps: {
                ...cardConfig.childrenProps,
                ...card.card.data,
              },
            };
          });
        }
        if (cardConfig.type !== updatedCardConfig.card.type) {
          return cardConfig;
        }
        return {
          ...cardConfig,
          childrenProps: {
            ...cardConfig.childrenProps,
            ...updatedCardConfig.card.data,
          },
        };
      });

      return newCardsConfig;
    });
  };

  useEffect(() => {
    if (dashboardHistoryCards.length > 0) {
      dashboardHistoryCards.forEach((card) => {
        updateCards(card);
      });
    }
  }, [dashboardHistoryCards]);

  useEffect(() => {
    if (!shouldKeepSSEConnectionOpen) {
      return;
    }

    if (!user) {
      console.debug("DashboardContextProvider sem usuário");
      return;
    }

    // FIXME: obter usinaSelecionada
    const selectedUsinaId = selectedDashboardUsina?.usinaIds.ropInputId || 0;

    const clientId = selectedDashboardClient?.id || "";
    const dadosIndicadoresEvtManager = new DadosIndicadoresEventManager(
      selectedUsinaId,
      clientId,
      user.userAccessToken
    );

    dadosIndicadoresEvtManager.on("connected", () => {
      console.debug("Conectado Tempo Real DadosIndicadores");
    });
    dadosIndicadoresEvtManager.on("disconnected", (reason) => {
      console.debug("Desconectado Tempo Real DadosIndicadores:", { reason });
    });
    dadosIndicadoresEvtManager.on("reconnecting", (reason) => {
      console.debug("Reconectando Tempo Real DadosIndicadores:", { reason });
    });
    dadosIndicadoresEvtManager.on("error", (errorMessage) => {
      console.debug("Erro Tempo Real DadosIndicadores:", errorMessage);
      if (errorMessage.includes("Número máximo de conexões simultâneas")) {
        const newNotifications = {
          isAdding: true,
          notifications: [
            ...toastNotifications.notifications,
            {
              severity: "warning" as const,
              text: "Número máximo de conexões simultâneas do cliente atingido.",
            },
          ],
        };
        setToastNotifications(newNotifications);
      }
    });

    dadosIndicadoresEvtManager.on("ropCardUpdate", (ropCardUpdate) => {
      console.debug("Atualização Tempo Real Cards:", ropCardUpdate);
      if (!ropCardUpdate) return;

      let updatedCardConfig: CardTypeAndData;
      try {
        updatedCardConfig = ropCardEventToCard(ropCardUpdate);
      } catch (error) {
        console.error(
          error,
          "Erro ao converter evento de card:",
          ropCardUpdate
        );
        return;
      }

      if (
        dashboardHistoryCards.find(
          (card) => card.ropCard.type === updatedCardConfig.ropCard.type
        )
      ) {
        return;
      }
      console.debug("Card atualizado:", updatedCardConfig);
      updateCards(updatedCardConfig);
    });

    dadosIndicadoresEvtManager.on("ropActiveAlarmUpdate", (ropAlarmUpdate) => {
      console.debug(
        "Atualização em tempo real Alarmes Ativos:",
        ropAlarmUpdate
      );

      if (!ropAlarmUpdate) return;
      let updatedPortfolio: AlarmNotificationProps[];
      try {
        updatedPortfolio =
          ropSSEAlarmToAlarmNotificationPropsArray(ropAlarmUpdate);
      } catch (error) {
        console.error(
          error,
          "Erro ao converter evento dos dados do portfólio:",
          ropAlarmUpdate
        );
        return;
      }
      console.debug("Portfolio atualizado:", updatedPortfolio);
      setActiveAlarms(updatedPortfolio);
    });
    return () => {
      dadosIndicadoresEvtManager.destroy();
    };
  }, [
    shouldKeepSSEConnectionOpen,
    user,
    selectedDashboardClient,
    selectedDashboardUsina,
    dashboardHistoryCards,
  ]);

  // New effect for DadosPortfolio event handling
  useEffect(() => {
    if (!shouldKeepSSEConnectionOpen) {
      return;
    }

    if (!user) {
      console.debug("DadosPortfolioEventManager sem usuário");
      return;
    }

    const userId = user.userId;
    const clientId = selectedClientFilter?.id || "";
    const clusterId = selectedCluster?.id || "";

    const dadosPortfolioEvtManager = new DadosPortfolioEventManager(
      userId,
      clientId,
      user.userAccessToken,
      clusterId
    );

    dadosPortfolioEvtManager.on("connected", () => {
      console.debug("Conectado Tempo Real DadosPortfolio");
    });
    dadosPortfolioEvtManager.on("disconnected", (reason) => {
      console.debug("Desconectado Tempo Real DadosPortfolio:", { reason });
    });
    dadosPortfolioEvtManager.on("reconnecting", (reason) => {
      console.debug("Reconectando Tempo Real DadosPortfolio:", { reason });
    });
    dadosPortfolioEvtManager.on("error", (errorMessage) => {
      console.debug("Erro Tempo Real DadosPortfolio:", errorMessage);
      if (errorMessage.includes("Número máximo de conexões simultâneas")) {
        const newNotifications = {
          isAdding: true,
          notifications: [
            ...toastNotifications.notifications,
            {
              severity: "warning" as const,
              text: "Número máximo de conexões simultâneas do cliente atingido.",
            },
          ],
        };
        setToastNotifications(newNotifications);
      }
    });

    dadosPortfolioEvtManager.on("portfolioUpdate", (portfolioUpdate) => {
      console.debug("Atualização Tempo Real Portfolio:", portfolioUpdate);

      if (!portfolioUpdate) return;
      let updatedPortfolio: PortfolioData;
      try {
        updatedPortfolio = ropSSEEventToPortfolioData(portfolioUpdate);
      } catch (error) {
        console.error(
          error,
          "Erro ao converter evento dos dados do portfólio:",
          portfolioUpdate
        );
        return;
      }
      console.debug("Portfolio atualizado:", updatedPortfolio);
      setPortfolioData(updatedPortfolio);
    });

    return () => {
      dadosPortfolioEvtManager.destroy();
    };
  }, [shouldKeepSSEConnectionOpen, user, selectedClientFilter, selectedCluster]);

  async function updateUserResetLayout(userId: string) {
    await usersApi.resetLayoutToFalse(userId);
  }

  useEffect(() => {
    if (user?.resetLayout) {
      setCardsConfig(getDefaultCardsConfig());
      setLayoutsConfig(defaultLayouts);
      setCardsDisplayableProps(defaultCardsDisplayableProps);

      updateUserResetLayout(user.userId);
    }
  }, [user?.resetLayout])

  useEffect(() => {
    if (!selectedDashboardUsina) return;

    const newNotifications = {
      isAdding: true,
      notifications: [
        ...toastNotifications.notifications,
        {
          severity: "success" as const,
          text: `${selectedDashboardUsina?.name} selecionada.`,
        },
      ],
    };
    setToastNotifications(newNotifications);
    setCardsConfig(getDefaultCardsConfig());
    setEquipmentFilter({});
  }, [selectedDashboardUsina]);

  function onMount() {
    setLayoutsConfig(getStoredLayouts());
    setCardsDisplayableProps(getStoredDisplayableProps());
    setCardsDisplayableColorProps(getStoredDisplayableColorProps());
    setCardsConfig(getStoredCardsConfig());
  }

  // useEffect(() => {
  //   setCardsConfig(getDefaultCardsConfig(info.value));
  // }, [theme]);

  const toggleStaticLayouts = (canEdit: boolean) => {
    setLayoutsConfig({
      ...layoutsConfig,
      layouts: {
        lg: layoutsConfig.layouts.lg?.map((l) => ({ ...l, static: !canEdit })),
        md: layoutsConfig.layouts.md?.map((l) => ({ ...l, static: !canEdit })),
        sm: layoutsConfig.layouts.sm?.map((l) => ({ ...l, static: !canEdit })),
        xs: layoutsConfig.layouts.xs?.map((l) => ({ ...l, static: !canEdit })),
        xxs: layoutsConfig.layouts.xxs?.map((l) => ({
          ...l,
          static: !canEdit,
        })),
      },
    });
  };
  useEffect(() => {
    localStorage.setItem("layoutsConfig", JSON.stringify(layoutsConfig));
  }, [layoutsConfig]);

  useEffect(() => {
    const cardConfigIdentifiers = cardsConfig.map((cardConfig) =>
      cardInfoToCardInfoIdentifiers(cardConfig)
    );
    localStorage.setItem("cardsConfig", JSON.stringify(cardConfigIdentifiers));
    console.debug("cardsConfig update", cardsConfig);
  }, [cardsConfig]);

  useEffect(() => {
    toggleStaticLayouts(canEditLayout);
  }, [canEditLayout]);

  useEffect(() => {
    const newCardsConfig = getCardsInfoWithUpdatedProps(
      cardsConfig,
      cardsDisplayableProps
    );

    setCardsConfig(newCardsConfig);
    localStorage.setItem(
      "cardsDisplayableProps",
      JSON.stringify(cardsDisplayableProps)
    );
  }, [cardsDisplayableProps]);

  useEffect(() => {
    localStorage.setItem(
      "cardsDisplayableColorProps",
      JSON.stringify(cardsDisplayableColorProps)
    );
  }, [cardsDisplayableColorProps]);

  function getChildrenPropsByCardType(
    cardType: CardTypesEnum
  ): CardChildrenProps | undefined {
    return cardsConfig.find((cardConfig) => cardConfig.type === cardType)
      ?.childrenProps;
  }
  return (
    <DashboardContext.Provider
      value={{
        layoutsConfig,
        setLayoutsConfig,
        defaultLayouts,
        cardsConfig,
        setCardsConfig,
        cardsDisplayableProps,
        setCardsDisplayableProps,
        cardsDisplayableColorProps,
        setCardsDisplayableColorProps,
        canEditLayout,
        setCanEditLayout,
        getChildrenPropsByCardType,
        portfolioData,
        showPotenciaAtivaInCharts,
        setShowPotenciaAtivaInCharts,
        activeAlarms,
        requestParamCards,
        setRequestParamCards,
        dashboardHistoryCards,
        equipmentFilter,
        setEquipmentFilter,
        cardIsLoading,
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  );
}
